/**
 * Centralized API client for the Hono backend.
 *
 * - Automatically attaches the BetterAuth session token.
 * - Typed convenience methods: get, post, put, patch, delete.
 * - Consistent error handling.
 *
 * All frontend hooks / controllers should use this instead of the Supabase
 * client for data access.
 */

import { authClient } from './auth-client';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

/**
 * Hard wall-clock budget for every request (BUG-87).
 *
 * `fetch` has NO default timeout: a hung upstream (a proxy holding the socket
 * open, a stale internal address — cf. BUG-86) leaves the promise pending
 * forever. Callers that `await` it then never settle, so their `finally`
 * cleanup never runs. That is exactly how the demo assistant's `isProcessing`
 * flag got pinned true and the chat "processed" forever with no answer and no
 * error. Every request now fails loudly instead of hanging silently.
 */
const DEFAULT_TIMEOUT_MS = 20_000;
/** Session lookups sit in front of every request, so they get a tighter budget. */
const SESSION_TIMEOUT_MS = 8_000;

/** Reject with `reason` if `p` has not settled within `ms`. */
function withTimeout<T>(p: Promise<T>, ms: number, reason: () => Error): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(reason()), ms);
    p.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e) => {
        clearTimeout(timer);
        reject(e);
      },
    );
  });
}

// ---------------------------------------------------------------------------
// Error type
// ---------------------------------------------------------------------------

export class ApiError extends Error {
  status: number;
  data: unknown;

  constructor(message: string, status: number, data?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Session cache — avoid hammering /api/auth/get-session on every API call.
// The BetterAuth cookie is sent automatically via `credentials: 'include'`,
// so we only need the token for the Authorization header (optional fallback).
// Cache the session for 30 seconds to avoid redundant network calls.
// ---------------------------------------------------------------------------
let _cachedSession: { token: string | null; expiresAt: number } | null = null;
let _sessionFetchPromise: Promise<string | null> | null = null;

const SESSION_CACHE_MS = 30_000; // 30 seconds

async function getCachedSessionToken(): Promise<string | null> {
  // Return cached token if still fresh
  if (_cachedSession && Date.now() < _cachedSession.expiresAt) {
    return _cachedSession.token;
  }

  // Deduplicate in-flight requests
  if (_sessionFetchPromise) {
    return _sessionFetchPromise;
  }

  _sessionFetchPromise = (async () => {
    try {
      // BUG-87: bounded — a hung session lookup would otherwise stall every
      // subsequent request before its own timeout could ever fire.
      const session = await withTimeout(
        Promise.resolve(authClient.getSession()),
        SESSION_TIMEOUT_MS,
        () => new ApiError('Session lookup timed out', 408),
      );
      const token = session?.data?.session?.token ?? null;
      _cachedSession = { token, expiresAt: Date.now() + SESSION_CACHE_MS };
      return token;
    } catch {
      _cachedSession = { token: null, expiresAt: Date.now() + SESSION_CACHE_MS };
      return null;
    } finally {
      _sessionFetchPromise = null;
    }
  })();

  return _sessionFetchPromise;
}

/** Call this when the user signs in or out to reset the session cache. */
export function invalidateSessionCache() {
  _cachedSession = null;
  _sessionFetchPromise = null;
}

async function getHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const token = await getCachedSessionToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
}

function buildUrl(path: string, params?: Record<string, string>): string {
  const url = new URL(`/api${path}`, API_URL);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, value);
      }
    });
  }
  return url.toString();
}

async function request<T>(
  method: string,
  path: string,
  options?: {
    body?: unknown;
    params?: Record<string, string>;
    /** Override the default wall-clock budget for this call. */
    timeoutMs?: number;
  },
): Promise<T> {
  const headers = await getHeaders();
  const url = buildUrl(path, options?.params);
  const timeoutMs = options?.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  let res: Response;
  try {
    res = await fetch(url, {
      method,
      headers,
      credentials: 'include',
      body: options?.body ? JSON.stringify(options.body) : undefined,
      // BUG-87: never hang. A stalled upstream now surfaces as a 408 the UI
      // can render, instead of a promise that never settles.
      signal: AbortSignal.timeout(timeoutMs),
    });
  } catch (err) {
    const name = (err as Error)?.name;
    if (name === 'TimeoutError' || name === 'AbortError') {
      throw new ApiError(`Request timed out after ${timeoutMs}ms`, 408);
    }
    throw err;
  }

  // Handle non-JSON responses (e.g. 204 No Content)
  const contentType = res.headers.get('content-type');
  if (!contentType?.includes('application/json')) {
    if (!res.ok) {
      throw new ApiError(res.statusText || 'Request failed', res.status);
    }
    return undefined as T;
  }

  const json = await res.json();

  if (!res.ok) {
    throw new ApiError(
      json.error || json.message || 'Request failed',
      res.status,
      json,
    );
  }

  return json as T;
}

// ---------------------------------------------------------------------------
// Supabase .from() compatibility shim — SEED/DEMO SCRIPTS ONLY
// ---------------------------------------------------------------------------
// All production callers have been migrated to apiClient.get/post/put/patch/delete.
// This shim exists solely for seed scripts (seed-messages.ts, seed-invoices.ts,
// seed-bills.ts, seed-fmb-data.ts) which are dev-only and will be removed
// when seed scripts are migrated to the API server.

const TABLE_TO_ROUTE: Record<string, string> = {
  invoices: '/invoices',
  payments: '/payments',
  bills: '/bills',
  accounts: '/accounts',
  suppliers: '/suppliers',
  buyers: '/buyers',
  messages: '/messages',
  quotes: '/quotes',
  documents: '/documents',
  alerts: '/alerts',
  financial_metrics: '/financial-metrics',
  companies: '/companies',
};

function createQueryShim(table: string) {
  const apiRoute = TABLE_TO_ROUTE[table];
  let isWrite = false;
  let writeData: any = null;

  const shim: any = {
    select: (..._args: any[]) => shim,
    insert: (data: any) => { isWrite = true; writeData = data; return shim; },
    update: (data: any) => { isWrite = true; writeData = data; return shim; },
    upsert: (data: any) => { isWrite = true; writeData = data; return shim; },
    delete: (..._args: any[]) => { isWrite = true; return shim; },
    eq: (..._args: any[]) => shim,
    neq: (..._args: any[]) => shim,
    gt: (..._args: any[]) => shim,
    gte: (..._args: any[]) => shim,
    lt: (..._args: any[]) => shim,
    lte: (..._args: any[]) => shim,
    like: (..._args: any[]) => shim,
    ilike: (..._args: any[]) => shim,
    is: (..._args: any[]) => shim,
    in: (..._args: any[]) => shim,
    order: (..._args: any[]) => shim,
    limit: (..._args: any[]) => shim,
    range: (..._args: any[]) => shim,
    single: (..._args: any[]) => shim,
    maybeSingle: (..._args: any[]) => shim,
    match: (..._args: any[]) => shim,
    filter: (..._args: any[]) => shim,
    or: (..._args: any[]) => shim,
    not: (..._args: any[]) => shim,
    then: (resolve: (v: any) => void, _reject?: (e: any) => void) => {
      if (!apiRoute || isWrite) {
        return resolve({ data: isWrite ? writeData : null, error: null, count: null });
      }
      return request<any>('GET', apiRoute)
        .then((response) => {
          const data = response?.data ?? response ?? [];
          resolve({
            data: Array.isArray(data) ? data : [data],
            error: null,
            count: Array.isArray(data) ? data.length : null,
          });
        })
        .catch(() => {
          resolve({ data: [], error: null, count: 0 });
        });
    },
    catch: (_handler?: any) => shim,
  };

  return shim;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export const apiClient = {
  get<T>(path: string, params?: Record<string, string>, opts?: { timeoutMs?: number }): Promise<T> {
    return request<T>('GET', path, { params, timeoutMs: opts?.timeoutMs });
  },

  post<T>(path: string, body?: unknown, opts?: { timeoutMs?: number }): Promise<T> {
    return request<T>('POST', path, { body, timeoutMs: opts?.timeoutMs });
  },

  put<T>(path: string, body?: unknown): Promise<T> {
    return request<T>('PUT', path, { body });
  },

  patch<T>(path: string, body?: unknown): Promise<T> {
    return request<T>('PATCH', path, { body });
  },

  delete<T>(path: string): Promise<T> {
    return request<T>('DELETE', path);
  },

  /**
   * @deprecated SEED/DEMO SCRIPTS ONLY. Do not use in production code.
   * Use apiClient.get/post/put/patch/delete instead.
   */
  from(table: string) {
    if (typeof window !== 'undefined') {
      const isProduction = window.location.hostname !== 'localhost';
      const logFn = isProduction ? console.error : console.warn;
      logFn(
        `[apiClient.from] DEPRECATED: table "${table}". Migrate to apiClient.get/post.`,
        new Error().stack,
      );
    }
    return createQueryShim(table);
  },
};
